import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { IndianRupee, Pencil, History, Plus, Wallet2 } from "lucide-react";
import { toast } from "sonner";
import SalaryStructureFormDialog, {
  computeSalary, fmtINR,
} from "@/components/app/SalaryStructureFormDialog";
import SalaryRevisionHistoryDialog from "@/components/app/SalaryRevisionHistoryDialog";

const todayISO = () => new Date().toISOString().slice(0, 10);

const emptyForm = {
  id: null,
  user_id: "",
  user_name: "",
  basic: 0,
  hra: 0,
  special_allowance: 0,
  esi: 0,
  pt: 200,
  pf_applicable: false,
  hra_applicable: true,
  effective_from: todayISO(),
  note: "",
};

export default function SalaryStructures() {
  const { user } = useAuth();
  const { selectedId } = useProperty();
  const [staff, setStaff] = useState([]);
  const [structures, setStructures] = useState([]);
  const [historyUser, setHistoryUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const isSuper = user?.role === "super_admin";

  const load = useCallback(async () => {
    try {
      const propertyParam = selectedId ? `?property_id=${selectedId}` : "";
      const [s, ss] = await Promise.all([
        api.get(`/users${propertyParam}`).catch(() => ({ data: [] })),
        api.get(`/salary-structures${propertyParam}`),
      ]);
      setStaff(s.data);
      setStructures(ss.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load salary structures");
    }
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  // Map user_id -> latest (effective_from <= today) structure
  const activeByUser = useMemo(() => {
    const today = todayISO();
    const m = {};
    for (const r of structures) {
      if (r.effective_from > today) continue;
      const cur = m[r.user_id];
      if (!cur || r.effective_from > cur.effective_from) m[r.user_id] = r;
    }
    return m;
  }, [structures]);

  const historyForUser = useMemo(() => {
    if (!historyUser) return [];
    return structures
      .filter((r) => r.user_id === historyUser.id)
      .sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1));
  }, [structures, historyUser]);

  const filteredStaff = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return staff;
    return staff.filter((u) =>
      [u.name, u.employee_id, u.role, u.email].filter(Boolean).some((x) => x.toLowerCase().includes(f))
    );
  }, [staff, filter]);

  const openCreate = (u) => {
    const active = activeByUser[u.id];
    setForm(active
      ? { ...active, id: null, user_name: u.name, effective_from: todayISO(), note: "" }
      : { ...emptyForm, user_id: u.id, user_name: u.name });
    setOpen(true);
  };

  const openEdit = (r) => {
    setForm({ ...r });
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        user_id: form.user_id,
        basic: Number(form.basic) || 0,
        hra: Number(form.hra) || 0,
        special_allowance: Number(form.special_allowance) || 0,
        esi: Number(form.esi) || 0,
        pt: Number(form.pt) || 0,
        pf_applicable: !!form.pf_applicable,
        hra_applicable: !!form.hra_applicable,
        effective_from: form.effective_from,
        note: form.note || null,
      };
      if (form.id) {
        await api.put(`/salary-structures/${form.id}`, payload);
        toast.success("Salary structure updated");
      } else {
        await api.post(`/salary-structures`, payload);
        toast.success("Salary structure created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete the ${r.effective_from} salary revision for ${r.user_name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/salary-structures/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const kpis = useMemo(() => {
    const rows = Object.values(activeByUser);
    const totals = { ctc: 0, basic: 0, hra: 0, special: 0, statutory: 0 };
    for (const r of rows) {
      const c = computeSalary(r);
      totals.ctc += c.ctc;
      totals.basic += Number(r.basic) || 0;
      totals.hra += (r.hra_applicable !== false ? Number(r.hra) || 0 : 0);
      totals.special += Number(r.special_allowance) || 0;
      totals.statutory += (Number(r.esi) || 0) + c.pfEmployer + (Number(r.pt) || 0);
    }
    return { ...totals, count: rows.length };
  }, [activeByUser]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Compensation</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Salary Structures</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-3xl">
            Define each employee&apos;s monthly salary structure. CTC = Basic + HRA + Special Allowance + ESI + PF + PT.
            Revisions are versioned by effective date and are auto-applied to the Payroll module.
          </p>
        </div>
        <div>
          <Input
            placeholder="Search employee, role…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-64"
            data-testid="ss-search"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SumCard label="Employees with structure" value={kpis.count} icon={Wallet2} />
        <SumCard label="Total Basic" value={fmtINR(kpis.basic)} />
        <SumCard label="Total HRA + Spl" value={fmtINR(kpis.hra + kpis.special)} />
        <SumCard label="Total Statutory" value={fmtINR(kpis.statutory)} accent="amber" />
        <SumCard label="Total Monthly CTC" value={fmtINR(kpis.ctc)} accent="green" />
      </div>

      <SalaryStructuresTable
        filteredStaff={filteredStaff}
        activeByUser={activeByUser}
        isSuper={isSuper}
        onOpenCreate={openCreate}
        onOpenEdit={openEdit}
        onOpenHistory={setHistoryUser}
      />

      <SalaryStructureFormDialog
        open={open}
        onOpenChange={setOpen}
        form={form}
        setForm={setForm}
        onSubmit={submit}
        busy={busy}
      />

      <SalaryRevisionHistoryDialog
        historyUser={historyUser}
        rows={historyForUser}
        isSuper={isSuper}
        onClose={() => setHistoryUser(null)}
        onEdit={(r) => { openEdit(r); setHistoryUser(null); }}
        onDelete={remove}
      />
    </div>
  );
}

function SalaryStructuresTable({ filteredStaff, activeByUser, isSuper, onOpenCreate, onOpenEdit, onOpenHistory }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
      <table className="w-full text-sm min-w-[1200px]">
        <thead className="bg-zinc-50 border-b border-zinc-200">
          <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
            <th className="py-3 px-3">Employee</th>
            <th className="py-3 px-3 text-right">Basic</th>
            <th className="py-3 px-3 text-right">HRA</th>
            <th className="py-3 px-3 text-right">Spl Allow.</th>
            <th className="py-3 px-3 text-right">Gross</th>
            <th className="py-3 px-3 text-center">PF</th>
            <th className="py-3 px-3 text-right">ESI</th>
            <th className="py-3 px-3 text-right">PT</th>
            <th className="py-3 px-3 text-right">CTC</th>
            <th className="py-3 px-3 text-right">Net</th>
            <th className="py-3 px-3 text-right">Effective</th>
            <th className="py-3 px-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredStaff.map((u) => (
            <SalaryRow
              key={u.id}
              u={u}
              r={activeByUser[u.id]}
              isSuper={isSuper}
              onOpenCreate={onOpenCreate}
              onOpenEdit={onOpenEdit}
              onOpenHistory={onOpenHistory}
            />
          ))}
          {filteredStaff.length === 0 && (
            <tr><td colSpan={12} className="py-10 text-center text-zinc-500">No staff in scope.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function SalaryRow({ u, r, isSuper, onOpenCreate, onOpenEdit, onOpenHistory }) {
  const c = r ? computeSalary(r) : null;
  return (
    <tr className="border-b border-zinc-100" data-testid={`ss-row-${u.id}`}>
      <td className="py-2.5 px-3">
        <div className="font-medium">{u.name}</div>
        <div className="text-[10px] text-zinc-500 font-mono">
          {u.employee_id || "—"} · {u.role.replace("_", " ")}
          {u.department ? ` · ${u.department}` : ""}
        </div>
      </td>
      {r ? (
        <>
          <td className="py-2.5 px-3 text-right font-mono">{fmtINR(r.basic)}</td>
          <td className="py-2.5 px-3 text-right font-mono text-zinc-600">
            {r.hra_applicable === false
              ? <span className="text-[10px] text-zinc-400">N/A</span>
              : fmtINR(r.hra)}
          </td>
          <td className="py-2.5 px-3 text-right font-mono text-zinc-600">{fmtINR(r.special_allowance)}</td>
          <td className="py-2.5 px-3 text-right font-mono">{fmtINR(c.gross)}</td>
          <td className="py-2.5 px-3 text-center">
            {r.pf_applicable
              ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">₹1,800 × 2</span>
              : <span className="text-[10px] text-zinc-400">Off</span>}
          </td>
          <td className="py-2.5 px-3 text-right font-mono text-zinc-600">{fmtINR(r.esi)}</td>
          <td className="py-2.5 px-3 text-right font-mono text-zinc-600">{fmtINR(r.pt)}</td>
          <td className="py-2.5 px-3 text-right font-mono font-bold text-emerald-700">{fmtINR(c.ctc)}</td>
          <td className="py-2.5 px-3 text-right font-mono text-zinc-700">{fmtINR(c.netTakeHome)}</td>
          <td className="py-2.5 px-3 text-right text-xs text-zinc-500">{r.effective_from}</td>
          <td className="py-2.5 px-3 text-right whitespace-nowrap">
            <button
              onClick={() => onOpenHistory(u)}
              className="p-1.5 hover:bg-zinc-100 rounded"
              title="View history"
              data-testid={`ss-history-${u.id}`}
            >
              <History className="w-3.5 h-3.5" />
            </button>
            {isSuper && (
              <>
                <button
                  onClick={() => onOpenCreate(u)}
                  className="p-1.5 hover:bg-zinc-100 rounded ml-1"
                  title="Add new revision"
                  data-testid={`ss-add-revision-${u.id}`}
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => onOpenEdit(r)}
                  className="p-1.5 hover:bg-zinc-100 rounded ml-1"
                  title="Edit current"
                  data-testid={`ss-edit-${u.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </td>
        </>
      ) : (
        <>
          <td colSpan={10} className="py-2.5 px-3 text-zinc-400 text-xs">
            No salary structure on record
          </td>
          <td className="py-2.5 px-3 text-right">
            {isSuper && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onOpenCreate(u)}
                data-testid={`ss-create-${u.id}`}
              >
                <IndianRupee className="w-3.5 h-3.5 mr-1" /> Set
              </Button>
            )}
          </td>
        </>
      )}
    </tr>
  );
}

function SumCard({ label, value, accent }) {
  const palette = {
    green: "bg-emerald-50 text-emerald-900 border-emerald-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
  };
  return (
    <div className={`bg-white border border-zinc-200 rounded-md p-4 ${accent ? palette[accent] : ""}`}>
      <div className="label-overline">{label}</div>
      <div className="text-2xl font-bold font-display mt-1">{value}</div>
    </div>
  );
}
